import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  Building2, Users, UserCheck, UserX, FileText, Activity,
  TrendingUp, Clock, ArrowRight, ShieldAlert, CheckCircle, XCircle, DollarSign, Mail, UserCircle
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingISOs, setPendingISOs] = useState([]);
  const [approving, setApproving] = useState(null);
  const [scheduleModal, setScheduleModal] = useState(null); // pending ISO being configured
  const [scheduleForm, setScheduleForm] = useState({ buy_rate_multiplier: 1, markup_flat_fee: 0, markup_pct_fee: 0, notes: '' });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [demoLeads, setDemoLeads] = useState([]);
  const [showDemoLeads, setShowDemoLeads] = useState(false);

  useEffect(() => {
    loadDashboard();
    loadPendingISOs();
    loadDemoLeads();
  }, []);

  const loadDemoLeads = async () => {
    try {
      const data = await api.getDemoLeads();
      setDemoLeads(data.leads || []);
    } catch (err) {
      console.error('Failed to load demo leads:', err);
    }
  };

  const loadDashboard = async () => {
    try {
      const data = await api.getSuperAdminDashboard();
      setStats(data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingISOs = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/onboarding/admin/pending-isos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPendingISOs(data.pending_isos || []);
      }
    } catch (err) {
      console.error('Failed to load pending ISOs:', err);
    }
  };

  const handleApprove = async (tenantId, name) => {
    setApproving(tenantId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/onboarding/admin/approve-iso/${tenantId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success(`${name} has been approved`);
        setPendingISOs(prev => prev.filter(p => p.tenant_id !== tenantId));
      }
    } catch {
      toast.error('Failed to approve');
    } finally {
      setApproving(null);
    }
  };

  const handleReject = async (tenantId, name) => {
    setApproving(tenantId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/onboarding/admin/reject-iso/${tenantId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success(`${name} has been rejected`);
        setPendingISOs(prev => prev.filter(p => p.tenant_id !== tenantId));
      }
    } catch {
      toast.error('Failed to reject');
    } finally {
      setApproving(null);
    }
  };

  const openScheduleModal = (iso) => {
    setScheduleModal(iso);
    setScheduleForm({ buy_rate_multiplier: 1, markup_flat_fee: 0, markup_pct_fee: 0, notes: '' });
  };

  const saveCostSchedule = async () => {
    if (!scheduleModal) return;
    setSavingSchedule(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/residuals/cost-schedule`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: scheduleModal.tenant_id,
          processor: 'maverick',
          buy_rate_multiplier: parseFloat(scheduleForm.buy_rate_multiplier) || 1,
          markup_flat_fee: parseFloat(scheduleForm.markup_flat_fee) || 0,
          markup_pct_fee: parseFloat(scheduleForm.markup_pct_fee) || 0,
          notes: scheduleForm.notes,
        }),
      });
      if (res.ok) {
        toast.success(`MSC Cost Schedule saved for ${scheduleModal.company_name}`);
        setPendingISOs(prev => prev.map(p => p.tenant_id === scheduleModal.tenant_id ? { ...p, cost_schedule_status: 'configured' } : p));
        setScheduleModal(null);
      } else {
        toast.error('Failed to save cost schedule');
      }
    } catch {
      toast.error('Failed to save cost schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const metrics = [
    { label: 'Total Tenants', value: stats?.total_tenants ?? 0, icon: Building2, color: 'blue', bg: 'bg-blue-100', text: 'text-blue-600' },
    { label: 'Active Tenants', value: stats?.active_tenants ?? 0, icon: TrendingUp, color: 'green', bg: 'bg-green-100', text: 'text-green-600' },
    { label: 'Suspended Tenants', value: stats?.suspended_tenants ?? 0, icon: ShieldAlert, color: 'red', bg: 'bg-red-100', text: 'text-red-600' },
    { label: 'Total Users', value: stats?.total_users ?? 0, icon: Users, color: 'indigo', bg: 'bg-indigo-100', text: 'text-indigo-600' },
    { label: 'Total Leads', value: stats?.total_leads ?? 0, icon: UserCheck, color: 'amber', bg: 'bg-amber-100', text: 'text-amber-600' },
    { label: 'Total Applications', value: stats?.total_applications ?? 0, icon: FileText, color: 'purple', bg: 'bg-purple-100', text: 'text-purple-600' },
  ];

  const formatAction = (action) => {
    return action?.replace(/\./g, ' ').replace(/_/g, ' ') ?? '';
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <>
    <div className="space-y-6" data-testid="super-admin-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Platform Overview</h1>
          <p className="text-sm text-muted-foreground mt-1">Super Admin Dashboard</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowDemoLeads(!showDemoLeads)} data-testid="demo-leads-btn">
            <Mail className="h-4 w-4 mr-2" /> Demo Leads {demoLeads.length > 0 && <span className="ml-1 bg-indigo-100 text-indigo-700 rounded-full px-2 text-xs font-semibold">{demoLeads.length}</span>}
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/tenants')} data-testid="manage-tenants-btn">
            <Building2 className="h-4 w-4 mr-2" /> Manage Tenants
          </Button>
          <Button className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={() => navigate('/admin/users')} data-testid="manage-users-btn">
            <Users className="h-4 w-4 mr-2" /> Manage Users
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {metrics.map((m) => {
          const Icon = m.icon;
          return (
            <Card key={m.label} className="p-6" data-testid={`stat-${m.label.toLowerCase().replace(/ /g, '-')}`}>
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">{m.label}</p>
                  <h3 className="text-3xl font-bold">{m.value}</h3>
                </div>
                <div className={`w-12 h-12 ${m.bg} rounded-lg flex items-center justify-center`}>
                  <Icon className={`h-6 w-6 ${m.text}`} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Demo Leads Panel */}
      {showDemoLeads && (
        <Card className="p-6" data-testid="demo-leads-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg flex items-center gap-2"><Mail className="h-5 w-5 text-indigo-500" /> Test Drive Prospects</h3>
            <span className="text-sm text-muted-foreground">{demoLeads.length} total</span>
          </div>
          {demoLeads.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No prospects yet. They'll appear here after someone requests a demo.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left pb-2 font-medium text-muted-foreground">Name</th>
                    <th className="text-left pb-2 font-medium text-muted-foreground">Email</th>
                    <th className="text-left pb-2 font-medium text-muted-foreground">Company</th>
                    <th className="text-left pb-2 font-medium text-muted-foreground">Role</th>
                    <th className="text-left pb-2 font-medium text-muted-foreground">Verified</th>
                    <th className="text-left pb-2 font-medium text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {demoLeads.map(lead => (
                    <tr key={lead.id} className="hover:bg-muted/30" data-testid="demo-lead-row">
                      <td className="py-2.5 pr-4 font-medium">{lead.name}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{lead.email}</td>
                      <td className="py-2.5 pr-4">{lead.company || '—'}</td>
                      <td className="py-2.5 pr-4">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                          {lead.role === 'iso' ? 'ISO Owner' : lead.role === 'iso_agent' ? 'Agent' : 'Subagent'}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4">
                        {lead.verified
                          ? <span className="flex items-center gap-1 text-green-600"><CheckCircle className="h-3.5 w-3.5" /> Yes</span>
                          : <span className="flex items-center gap-1 text-amber-500"><Clock className="h-3.5 w-3.5" /> Pending</span>}
                      </td>
                      <td className="py-2.5 text-muted-foreground text-xs">{lead.created_at ? timeAgo(lead.created_at) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Quick Actions + Recent Activity */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Quick Actions */}
        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/admin/tenants?action=create')} data-testid="quick-create-tenant-btn">
              <span className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Create Tenant</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/admin/users?action=invite')} data-testid="quick-invite-user-btn">
              <span className="flex items-center gap-2"><Users className="h-4 w-4" /> Invite User</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" className="w-full justify-between" onClick={() => navigate('/admin/audit-logs')} data-testid="quick-audit-logs-btn">
              <span className="flex items-center gap-2"><Activity className="h-4 w-4" /> View Audit Logs</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </Card>

        {/* Recent Activity */}
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">Recent Activity</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/audit-logs')}>View All</Button>
          </div>
          <div className="space-y-4">
            {(stats?.recent_activity ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">No recent activity</p>
            )}
            {(stats?.recent_activity ?? []).map((log, idx) => (
              <div key={log.id || idx} className="flex items-start gap-3 pb-3 border-b last:border-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Activity className="h-4 w-4 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium capitalize">{formatAction(log.action)}</p>
                  <p className="text-xs text-muted-foreground">
                    {log.entity_type} {log.entity_id ? `(${log.entity_id.slice(-6)})` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                  <Clock className="h-3 w-3" />
                  <span>{formatTime(log.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Pending ISO Applications */}
      {pendingISOs.length > 0 && (
        <Card className="p-6" data-testid="pending-isos-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h3 className="font-semibold text-lg">Pending ISO Applications</h3>
              <span className="px-2.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">{pendingISOs.length}</span>
            </div>
          </div>
          <div className="space-y-3">
            {pendingISOs.map((iso) => (
              <div key={iso.tenant_id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors" data-testid={`pending-iso-${iso.tenant_id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{iso.company_name || iso.legal_business_name || 'New ISO'}</p>
                    <p className="text-xs text-muted-foreground">
                      {iso.owner_email} {iso.subdomain ? `• ${iso.subdomain}.isocntrl.com` : ''}
                    </p>
                    <span
                      className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        iso.cost_schedule_status === 'configured' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                      }`}
                      data-testid={`cost-schedule-status-${iso.tenant_id}`}
                    >
                      {iso.cost_schedule_status === 'configured' ? 'Cost Schedule Configured' : 'Awaiting Cost Schedule Setup'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">Step {iso.onboarding_step || 1}/5</span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openScheduleModal(iso)}
                      data-testid={`setup-cost-schedule-${iso.tenant_id}`}
                    >
                      <DollarSign className="h-4 w-4 mr-1" /> Setup Cost Schedule
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      disabled={approving === iso.tenant_id}
                      onClick={() => handleReject(iso.tenant_id, iso.company_name)}
                      data-testid={`reject-iso-${iso.tenant_id}`}
                    >
                      <XCircle className="h-4 w-4 mr-1" /> Reject
                    </Button>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      disabled={approving === iso.tenant_id}
                      onClick={() => handleApprove(iso.tenant_id, iso.company_name)}
                      data-testid={`approve-iso-${iso.tenant_id}`}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" /> Approve
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>

      {/* Setup MSC Cost Schedule Modal */}
      <Dialog open={!!scheduleModal} onOpenChange={(open) => !open && setScheduleModal(null)}>
        <DialogContent data-testid="cost-schedule-dialog">
          <DialogHeader>
            <DialogTitle>MSC Cost Schedule — {scheduleModal?.company_name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            Defines what MSC actually earns from this ISO's processor residuals, independent of what
            the processor reports. Agent Schedule A splits are calculated against this, not the raw processor net.
          </p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium mb-1 block">Buy Rate Multiplier (1.0 = trust processor as-is)</label>
              <Input
                type="number" step="0.01" value={scheduleForm.buy_rate_multiplier}
                onChange={e => setScheduleForm(prev => ({ ...prev, buy_rate_multiplier: e.target.value }))}
                data-testid="cost-schedule-buy-rate-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium mb-1 block">Flat Markup Fee ($ / merchant / period)</label>
                <Input
                  type="number" step="0.01" value={scheduleForm.markup_flat_fee}
                  onChange={e => setScheduleForm(prev => ({ ...prev, markup_flat_fee: e.target.value }))}
                  data-testid="cost-schedule-flat-fee-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">Markup Fee (%)</label>
                <Input
                  type="number" step="0.01" value={scheduleForm.markup_pct_fee}
                  onChange={e => setScheduleForm(prev => ({ ...prev, markup_pct_fee: e.target.value }))}
                  data-testid="cost-schedule-pct-fee-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Notes</label>
              <Input
                value={scheduleForm.notes}
                onChange={e => setScheduleForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="e.g. agreement terms, effective date..."
                data-testid="cost-schedule-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleModal(null)}>Cancel</Button>
            <Button onClick={saveCostSchedule} disabled={savingSchedule} data-testid="save-cost-schedule-btn">
              {savingSchedule ? 'Saving...' : 'Save Cost Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
